/* eslint-disable no-param-reassign */
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Popconfirm, Space } from 'antd'
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, OrderedListOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-enterprise'
import { VariableType } from '../../../components/Table/types'
import { InvestmentSummaryInvestment } from '../../../types/investment/Investment'
import { formatValueByType } from '../../../utils/helpers'
import { investmentsPageSelector } from '../../../redux/pages/investments/selectors'
import { useFiscalYearToDateLabel } from './hooks'
import { useCtxDimensions } from '../../dimension/queries/useCtxDimensions'
import { useAccounts } from '../../account/queries/useFetchAccounts'
import DimensionName from '../../dimension/components/DimensionName'
import { parseDate } from '../../../config/dayjsConfig'
import { depreciableBalanceSheetRowIdsSelector } from '../../../redux/entities/statementRows/selectors'
import {
  acceleratedDeprecationDeltaRowIdsSelector,
  acceleratedDeprecationRowIdSelector,
  plannedDeprecationRowIdSelector
} from '../../../redux/config/financialStatement/selectors'
import {
  AccountSelectCellEditor,
  DimensionTreeSelectCellEditor,
  NumericCellEditor,
  SelectCellEditor
} from '../../../shared/components/custom/Grid/Editor'
import { DeprecationMethodId } from '../../../types/deprecation/DeprecationMethod'
import { DeprecationPlan } from '../../../types/deprecation/DeprecationPlan'

interface UseInvestmentColumnsOptions {
  investments: InvestmentSummaryInvestment[]
  isExisting: boolean
  isLocked?: boolean
  onDepreciationClick: (investment: InvestmentSummaryInvestment) => void
  onEditClick: (investment: InvestmentSummaryInvestment) => void
  onStartEdit: (investment: InvestmentSummaryInvestment) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onDelete: (investment: InvestmentSummaryInvestment) => void
}

export const useInvestmentColumns = ({
  investments,
  isExisting,
  isLocked = false,
  onDepreciationClick,
  onEditClick,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete
}: UseInvestmentColumnsOptions) => {
  const { t } = useTranslation()
  const { date } = useSelector(investmentsPageSelector)
  const { data: dimensions = [] } = useCtxDimensions()
  const { data: accounts } = useAccounts()
  const fiscalYearToDateLabel = useFiscalYearToDateLabel()
  const depreciableBalanceSheetRowIds = useSelector(depreciableBalanceSheetRowIdsSelector)
  const plannedDeprecationRowId = useSelector(plannedDeprecationRowIdSelector)
  const acceleratedDeprecationRowId = useSelector(acceleratedDeprecationRowIdSelector)
  const acceleratedDeprecationDeltaRowIds = useSelector(acceleratedDeprecationDeltaRowIdsSelector)

  const deprecationMethodOptions = useMemo(
    () => [
      { value: DeprecationMethodId.StraightLine, label: t('depreciationMethod:1') },
      { value: DeprecationMethodId.DecliningBalance, label: t('depreciationMethod:2') }
    ],
    [t]
  )

  const columnDefs = useMemo<(ColDef<InvestmentSummaryInvestment> | ColGroupDef<InvestmentSummaryInvestment>)[]>(() => {
    const editable = isExisting && !isLocked
    const cols: (ColDef<InvestmentSummaryInvestment> | ColGroupDef<InvestmentSummaryInvestment>)[] = [
      {
        headerName: t('investmentsPage:nameOfAsset'),
        field: 'name',
        editable,
        pinned: 'left',
        width: 140,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: t('investmentsPage:purchasePrice'),
        field: 'value',
        editable,
        pinned: 'left',
        width: 110,
        filter: 'agNumberColumnFilter',
        cellEditor: NumericCellEditor,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
      },
      {
        headerName: t('investmentsPage:acquisitionDate'),
        field: 'date',
        type: 'datePicker',
        editable,
        width: 110
      },
      {
        headerName: t('investmentsPage:accountCodeInAccounting'),
        field: 'accountCode',
        editable,
        width: 125,
        cellEditor: AccountSelectCellEditor,

        cellEditorParams: {
          balanceSheetRowIds: depreciableBalanceSheetRowIds
        },
        cellClass: 'ag-right-aligned-cell',
        filter: 'agSetColumnFilter'
      },
      {
        groupId: 'deprecationPlan',
        headerName: t('investmentsPage:deprecationFields'),
        children: [
          {
            headerName: t('financialStatementsPage:deprecation-method'),
            field: 'deprecationPlan.deprecationMethodId',
            editable,
            width: 125,
            cellEditor: SelectCellEditor,
            cellEditorParams: {
              options: deprecationMethodOptions
            },
            cellClass: 'ag-right-aligned-cell',
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: (params: { value: number }) =>
                params.value ? t(`depreciationMethod:${params.value}`) : ''
            },
            valueSetter: params => {
              params.data.deprecationPlan = { ...params.data.deprecationPlan, deprecationMethodId: params.newValue }
              return true
            },
            valueFormatter: params => (params.value ? t(`depreciationMethod:${params.value}`) : '')
          },
          {
            headerName: t('investmentsPage:startDate'),
            field: 'deprecationPlan.startDate',
            type: 'datePicker',
            editable,
            width: 115,
            valueSetter: params => {
              params.data.deprecationPlan = { ...params.data.deprecationPlan, startDate: params.newValue }
              return true
            }
          },
          {
            headerName: t('investmentsPage:percentageOrYear'),
            field: 'deprecationPlan.value',
            type: 'numeric',
            editable,
            width: 60,
            filter: 'agNumberColumnFilter',
            cellEditorParams: { decimals: 4 },
            valueFormatter: params => {
              if (params.value == null) return ''
              if (params.data?.deprecationPlan?.deprecationMethodId === DeprecationMethodId.DecliningBalance)
                return `${formatValueByType(params.value, VariableType.absolute, { maximumFractionDigits: 4 })} %`
              return formatValueByType(params.value, VariableType.absolute, { maximumFractionDigits: 0 })
            },
            valueSetter: params => {
              params.data.deprecationPlan = { ...params.data.deprecationPlan, value: params.newValue }
              return true
            }
          },
          {
            headerName: t('investmentsPage:plAccountCode'),
            field: 'deprecationPlan.plAccountCode',
            editable,
            width: 90,
            cellEditor: AccountSelectCellEditor,
            cellEditorParams: {
              balanceSheetRowIds: plannedDeprecationRowId || []
            },
            cellClass: 'ag-right-aligned-cell',
            filter: 'agSetColumnFilter',
            valueSetter: params => {
              params.data.deprecationPlan = { ...params.data.deprecationPlan, plAccountCode: params.newValue }
              return true
            }
          },
          {
            headerName: t('investmentsPage:bsAccountCode'),
            field: 'deprecationPlan.bsAccountCode',
            editable,
            width: 90,
            cellEditor: AccountSelectCellEditor,

            cellEditorParams: (params: { data: InvestmentSummaryInvestment }) => {
              const account = accounts?.find(a => a.code === params.data?.accountCode)
              return {
                balanceSheetRowIds: depreciableBalanceSheetRowIds,
                selectableBalanceSheetRowId: account?.statementRowId
              }
            },
            cellClass: 'ag-right-aligned-cell',
            filter: 'agSetColumnFilter',
            valueSetter: params => {
              params.data.deprecationPlan = { ...params.data.deprecationPlan, bsAccountCode: params.newValue }
              return true
            }
          },
          {
            headerName: t('global:dimension'),
            field: 'deprecationPlan.dimensionId',
            editable,
            width: 110,
            cellEditor: DimensionTreeSelectCellEditor,
            cellClass: 'ag-right-aligned-cell',
            filter: 'agSetColumnFilter',
            valueSetter: params => {
              params.data.deprecationPlan = { ...params.data.deprecationPlan, dimensionId: params.newValue }
              return true
            },
            cellRenderer: (params: ICellRendererParams<InvestmentSummaryInvestment>) => {
              if (!params.value) return null
              return <DimensionName dimensionId={params.value} />
            },
            filterParams: {
              values: (params: { success: (values: string[]) => void }) => {
                params.success([...new Set(investments.map(inv => inv.deprecationPlan?.dimensionId).filter(Boolean))])
              },
              cellRenderer: (params: ICellRendererParams) => {
                if (!params.value) return '(empty)'
                const dim = dimensions?.find(d => d.dimensionId === params.value)
                return dim?.name || params.value
              }
            }
          },
          {
            groupId: 'deprecation',
            headerName: t('investmentsPage:deprecations'),
            openByDefault: true,
            children: [
              {
                headerName: parseDate(date)?.format('MM/YYYY') ?? '-',
                field: 'summary.deprecation',
                width: 80,
                cellClass: 'ag-right-aligned-cell',
                filter: 'agNumberColumnFilter',
                valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
              },
              {
                headerName: fiscalYearToDateLabel,
                field: 'summary.fiscalYearDeprecation',
                width: 90,
                cellClass: 'ag-right-aligned-cell',
                filter: 'agNumberColumnFilter',
                valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
              }
            ] as ColDef<InvestmentSummaryInvestment>[]
          },
          {
            groupId: 'residual',
            headerName: t('investmentsPage:residuals'),
            openByDefault: true,
            children: [
              {
                headerName: parseDate(date)?.format('MM/YYYY') ?? '-',
                field: 'summary.budgetResidual',
                width: 80,
                cellClass: 'ag-right-aligned-cell',
                filter: 'agNumberColumnFilter',
                valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
              }
            ] as ColDef<InvestmentSummaryInvestment>[]
          }
        ] as ColDef<InvestmentSummaryInvestment>[]
      },
      {
        headerName: t('investmentsPage:balanceSheetRow'),
        field: 'balanceSheetRowId',
        width: 105,
        cellClass: 'ag-right-aligned-cell',
        filter: false,
        valueFormatter: params => (params.value ? t(`balanceSheet:${params.value}`) : '')
      },
      {
        groupId: 'taxDepreciation',
        headerName: t('investmentsPage:taxDeprecationFields'),
        children: [
          {
            headerName: t('financialStatementsPage:deprecation-method'),
            field: 'taxDeprecationPlan.deprecationMethodId',
            editable,
            width: 100,
            cellEditor: SelectCellEditor,
            cellEditorParams: {
              options: deprecationMethodOptions
            },
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: (params: { value: number }) =>
                params.value ? t(`depreciationMethod:${params.value}`) : ''
            },
            valueSetter: params => {
              params.data.taxDeprecationPlan = {
                ...params.data.taxDeprecationPlan,
                deprecationMethodId: params.newValue
              } as DeprecationPlan
              return true
            },
            valueFormatter: params => (params.value ? t(`depreciationMethod:${params.value}`) : '')
          },
          {
            headerName: t('investmentsPage:startDate'),
            field: 'taxDeprecationPlan.startDate',
            type: 'datePicker',
            editable,
            width: 80,
            valueSetter: params => {
              params.data.taxDeprecationPlan = {
                ...params.data.taxDeprecationPlan,
                startDate: params.newValue
              } as DeprecationPlan
              return true
            }
          },
          {
            headerName: t('investmentsPage:percentageOrYear'),
            field: 'taxDeprecationPlan.value',
            type: 'numeric',
            editable,
            width: 80,
            cellEditorParams: { decimals: 4 },
            valueFormatter: params => {
              if (params.value == null) return ''
              if (params.data?.taxDeprecationPlan?.deprecationMethodId === DeprecationMethodId.DecliningBalance)
                return `${params.value.toLocaleString(undefined, { maximumFractionDigits: 4 })} %`
              return params.value.toLocaleString(undefined, { maximumFractionDigits: 0 })
            },
            valueSetter: params => {
              params.data.taxDeprecationPlan = {
                ...params.data.taxDeprecationPlan,
                value: params.newValue
              } as DeprecationPlan
              return true
            }
          },
          {
            headerName: t('investmentsPage:plAccountCode'),
            field: 'taxDeprecationPlan.plAccountCode',
            editable,
            width: 90,
            cellEditor: AccountSelectCellEditor,
            cellEditorParams: {
              balanceSheetRowIds: acceleratedDeprecationDeltaRowIds || []
            },
            cellClass: 'ag-right-aligned-cell',
            valueSetter: params => {
              params.data.taxDeprecationPlan = {
                ...params.data.taxDeprecationPlan,
                plAccountCode: params.newValue
              } as DeprecationPlan
              return true
            }
          },
          {
            headerName: t('investmentsPage:bsAccountCode'),
            field: 'taxDeprecationPlan.bsAccountCode',
            editable,
            width: 90,
            cellEditor: AccountSelectCellEditor,

            cellEditorParams: {
              balanceSheetRowIds: acceleratedDeprecationRowId ? [acceleratedDeprecationRowId] : []
            },
            cellClass: 'ag-right-aligned-cell',
            valueSetter: params => {
              params.data.taxDeprecationPlan = {
                ...params.data.taxDeprecationPlan,
                bsAccountCode: params.newValue
              } as DeprecationPlan
              return true
            }
          },
          {
            groupId: 'taxDeprecation',
            headerName: t('investmentsPage:deprecations'),
            openByDefault: true,
            children: [
              {
                headerName: parseDate(date)?.format('MM/YYYY') ?? '-',
                field: 'summary.taxDeprecation',
                width: 80,
                cellClass: 'ag-right-aligned-cell',
                filter: 'agNumberColumnFilter',
                valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
              },
              {
                headerName: fiscalYearToDateLabel,
                field: 'summary.taxFiscalYearDeprecation',
                width: 90,
                cellClass: 'ag-right-aligned-cell',
                filter: 'agNumberColumnFilter',
                valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
              }
            ] as ColDef<InvestmentSummaryInvestment>[]
          },
          {
            groupId: 'taxResidual',
            headerName: t('investmentsPage:residuals'),
            openByDefault: true,
            children: [
              {
                headerName: parseDate(date)?.format('MM/YYYY') ?? '-',
                field: 'summary.taxResidual',
                width: 80,
                cellClass: 'ag-right-aligned-cell',
                filter: 'agNumberColumnFilter',
                valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
              }
            ] as ColDef<InvestmentSummaryInvestment>[]
          }
        ] as ColDef<InvestmentSummaryInvestment>[]
      },
      {
        headerName: t('investmentsPage:depreciations'),
        colId: 'depreciations',
        width: 65,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<InvestmentSummaryInvestment>) => (
          <Button
            block
            size="small"
            style={{ marginRight: '8px' }}
            icon={<OrderedListOutlined />}
            onClick={() => params.data && onDepreciationClick(params.data)}
          />
        )
      }
    ]

    if (isExisting) {
      cols.push({
        headerName: t('global:actions'),
        colId: 'actions',
        pinned: 'right',
        width: 80,
        sortable: false,
        filter: false,
        editable: false,
        cellRenderer: (params: ICellRendererParams<InvestmentSummaryInvestment>) => {
          if (!params.data) return null
          const editingRowId = params.context?.editingRowIdRef?.current ?? null
          const isEditingThisRow = editingRowId === params.data.id

          if (isEditingThisRow) {
            return (
              <Space>
                <Button size="small" type="link" icon={<CheckOutlined />} onClick={onSaveEdit} />
                <Button size="small" type="link" icon={<CloseOutlined />} onClick={onCancelEdit} />
              </Space>
            )
          }

          return (
            <Space>
              <Button
                size="small"
                type="link"
                icon={<EditOutlined />}
                onClick={() => onStartEdit(params.data!)}
                disabled={editingRowId !== null || isLocked}
              />
              <Popconfirm
                placement="topRight"
                title={t('global:delete-confirm')}
                onConfirm={() => params.data && onDelete(params.data)}
              >
                <Button
                  danger
                  size="small"
                  type="link"
                  icon={<DeleteOutlined />}
                  disabled={editingRowId !== null || isLocked}
                />
              </Popconfirm>
            </Space>
          )
        }
      })
    } else {
      cols.push({
        headerName: t('global:actions'),
        colId: 'actions',
        pinned: 'right',
        width: 80,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => (
          <Space>
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEditClick(params.data)}
              disabled={isLocked}
            />
            <Popconfirm
              placement="topRight"
              title={t('global:delete-confirm')}
              onConfirm={() => params.data && onDelete(params.data)}
            >
              <Button danger size="small" type="link" icon={<DeleteOutlined />} disabled={isLocked} />
            </Popconfirm>
          </Space>
        )
      })
    }

    return cols
  }, [
    t,
    isExisting,
    isLocked,
    date,
    fiscalYearToDateLabel,
    depreciableBalanceSheetRowIds,
    plannedDeprecationRowId,
    acceleratedDeprecationRowId,
    acceleratedDeprecationDeltaRowIds,
    deprecationMethodOptions,
    investments,
    dimensions,
    accounts,
    onDepreciationClick,
    onEditClick,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete
  ])

  return { columnDefs }
}
